import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Droplet, Leaf, PiggyBank, Recycle, Scale, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clp, num, useApi } from "@/lib/api";

const Kpi = ({ icon: Icon, label, value, testId, accent }) => (
  <div className={`rounded-2xl p-5 border ${accent ? "bg-[#0a1f18] border-[#1b3d30] text-white" : "bg-white border-slate-200"}`}>
    <p className={`flex items-center gap-2 text-xs uppercase tracking-wider ${accent ? "text-slate-400" : "text-slate-500"}`}><Icon className="w-4 h-4 text-[#00c487]" />{label}</p>
    <p className={`font-display text-2xl sm:text-3xl font-bold mt-2 ${accent ? "text-[#00f2a1]" : "text-[#05130e]"}`} data-testid={testId}>{value}</p>
  </div>
);

const toCsv = (rows) => {
  const head = ["fecha", "codigo", "codigo_ler", "descripcion_ler", "peligroso", "estado_fisico", "material", "categoria", "toneladas", "tratamiento", "rut_destinatario", "razon_social_destinatario", "retc_destinatario", "gestor_autorizado", "producto_prioritario_rep", "co2_t"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [head.join(";"), ...rows.map((r) => head.map((h) => esc(r[h])).join(";"))].join("\n");
};

const download = (rows) => {
  const blob = new Blob(["\ufeff" + toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `respaldo-sinader-terraloop-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

export const EsgPanel = () => {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    api("get", "/me/esg").then((r) => setD(r.data)).catch(() => setD({ totals: {}, rows: [], company: {} }));
  }, [api]);
  if (!d) return null;
  const t = d.totals;
  return (
    <div className="space-y-6" data-testid="esg-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi accent icon={Scale} label="Toneladas desviadas de relleno" value={`${num(t.tons, 2)} t`} testId="esg-kpi-tons" />
        <Kpi icon={Recycle} label="Tasa de desvío (circularidad)" value={t.diversion_rate == null ? "—" : `${num(t.diversion_rate, 1)}%`} testId="esg-kpi-diversion" />
        <Kpi accent icon={Leaf} label="CO₂ eq. mitigado" value={`${num(t.co2_t, 2)} t`} testId="esg-kpi-co2" />
        <Kpi icon={Zap} label="Energía evitada" value={`${num(t.energy_mwh, 1)} MWh`} testId="esg-kpi-energy" />
        <Kpi icon={Droplet} label="Agua evitada" value={`${num(t.water_m3, 0)} m³`} testId="esg-kpi-water" />
        <Kpi icon={PiggyBank} label="Ahorro tasa de portón + flete" value={clp((t.gate_fee_saved || 0) + (t.freight_saved || 0))} testId="esg-kpi-savings" />
        <Kpi icon={PiggyBank} label="Ingresos por venta de scrap" value={clp(t.revenue)} testId="esg-kpi-revenue" />
        <Kpi icon={Scale} label="Toneladas computables metas REP" value={`${num(t.rep_tons, 2)} t`} testId="esg-kpi-rep" />
      </div>
      {t.diversion_rate == null && <p className="text-xs text-slate-500">Para ver la tasa de desvío, indica las toneladas generadas al año en <Link to="/perfil" className="text-[#00915f] underline">Mi empresa</Link>.</p>}
      {!d.company.is_ppp && <p className="text-xs text-slate-500">Las toneladas REP cuentan para metas solo si tu empresa es Productor de Productos Prioritarios (márcalo en Mi empresa).</p>}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-[#05130e]">Respaldo para declaración SINADER / RETC</p>
          <p className="text-sm text-slate-500">{d.rows.length} transacciones cerradas con RUT del destinatario, tratamiento y toneladas exactas.</p>
        </div>
        <Button variant="outline" disabled={!d.rows.length} onClick={() => download(d.rows)} className="rounded-full" data-testid="esg-sinader-download-btn"><Download className="w-4 h-4" />Descargar CSV</Button>
      </div>
      <p className="text-[11px] text-slate-400">Energía, agua y CO₂ se calculan con factores referenciales estimados por familia de material; reemplázalos por factores DEFRA/EPA homologados antes de reportar oficialmente.</p>
    </div>
  );
};
